<script lang="ts">
    import Modal from '@/components/Modal.svelte';
    import { Button } from '@/components/ui/button';
    import { router, useForm } from '@inertiajs/svelte';
    import { page } from '@inertiajs/svelte';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import FormContainer from '@/components/Form/FormContainer.svelte';
    import Input from '@/components/Form/Inputs/Input.svelte';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import Select from '@/components/Inputs/Select.svelte';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- available for future use
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import FilePond, { registerPlugin } from 'svelte-filepond';
    import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
    import 'filepond/dist/filepond.min.css';
    import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type { Field } from '@/types/types';
    import { toast } from 'svelte-sonner';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { evaluateFieldDependencies } from '@/lib/fieldDependencies';
    import type { DependencyMap } from '@/types/fieldDependency';
    import { uploadImages } from '@/lib/imageUpload';
    import Date from '@/components/Inputs/Date.svelte';
    import Signature from '@/components/Inputs/Signature.svelte';

    registerPlugin(FilePondPluginFileValidateSize);

    type Props = {
        show: boolean;
        blocks: (Blocks & { fields: Field[] })[];
        dependencyMap?: DependencyMap;
        jobId: string;
        handover?: any;
        handoverPersonOptions?: { value: string; label: string }[];
    };

    let { show = $bindable(), blocks, dependencyMap = {}, jobId, handover = null, handoverPersonOptions = [] }: Props = $props();

    // Signature states
    let inspectorSignature = $state<{ pointGroups: any[]; svg: string } | null>(null);
    let contractorSignature = $state<{ pointGroups: any[]; svg: string } | null>(null);
    let clientSignature = $state<{ pointGroups: any[]; svg: string } | null>(null);

    // Track whether user wants to replace existing signatures
     
    let replaceInspectorSig = $state(false);
     
    let replaceContractorSig = $state(false);
     
    let replaceClientSig = $state(false);

    const currentUserName = $page.props.auth?.user?.name ?? '';

    // Initialize form data - reactive to handover changes
    function buildFormData() {
        const formData: Record<string, any> = {};
        const isNew = !handover;
        for (const block of blocks) {
            for (const field of block.fields) {
                if (handover?.[field.column_name] != null) {
                    formData[field.column_name] = handover[field.column_name];
                } else if (isNew && field.column_name === 'handover_date') {
                    const now = new globalThis.Date();
                    formData[field.column_name] = now.toISOString().split('T')[0];
                } else if (isNew && field.column_name === 'handover_time') {
                    const now = new globalThis.Date();
                    formData[field.column_name] = now.toTimeString().slice(0, 5);
                } else if (isNew && field.column_name === 'handover_carried_out_by') {
                    formData[field.column_name] = currentUserName;
                } else {
                    formData[field.column_name] = null;
                }
            }
        }
        // Signature fields aren't block fields but must be initialised in useForm
        formData.inspector_signature = null;
        formData.contractor_signature = null;
        formData.client_signature = null;

        return formData;
    }

    let createForm = $state(useForm(buildFormData()));

    // Re-initialize form when handover changes (e.g., opening for edit)
    $effect(() => {
        if (handover !== undefined) {
            createForm = useForm(buildFormData());
        }
    });

    // FilePond state
    let pondFiles = $state<any[]>([]);
    let pendingImageFiles = $state<File[]>([]);

    function handleFilePondAddFile(err: any, fileItem: any) {
        if (!err) {
            pendingImageFiles = [...pendingImageFiles, fileItem.file];
        }
    }

    function handleFilePondRemoveFile(err: any, fileItem: any) {
        if (!err) {
            pendingImageFiles = pendingImageFiles.filter(f => f !== fileItem.file);
        }
    }

    /**
     * Convert SVG string to base64 PNG data URI
     */
    async function svgToBase64Png(svgString: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width || 400;
                canvas.height = img.height || 200;
                ctx?.drawImage(img, 0, 0);
                const dataUrl = canvas.toDataURL('image/png');
                resolve(dataUrl);
            };

            img.onerror = () => {
                reject(new Error('Failed to convert SVG to PNG'));
            };

            // Create a blob URL from the SVG
            const blob = new Blob([svgString], { type: 'image/svg+xml' });
            img.src = URL.createObjectURL(blob);
        });
    }

    function close() {
        show = false;
        pondFiles = [];
        pendingImageFiles = [];
        inspectorSignature = null;
        contractorSignature = null;
        clientSignature = null;
        replaceInspectorSig = false;
        replaceContractorSig = false;
        replaceClientSig = false;
    }

    async function submit(e: Event) {
        $createForm.processing = true;
        e.preventDefault();

        // Convert signatures to base64 PNG and add to form data
        try {
            if (inspectorSignature?.svg) {
                const base64 = await svgToBase64Png(inspectorSignature.svg);
                $createForm.inspector_signature = base64;
            }
            if (contractorSignature?.svg) {
                const base64 = await svgToBase64Png(contractorSignature.svg);
                $createForm.contractor_signature = base64;
            }
            if (clientSignature?.svg) {
                const base64 = await svgToBase64Png(clientSignature.svg);
                $createForm.client_signature = base64;
            }
        } catch (error) {
            console.error('Error converting signatures:', error);
            toast.error('Failed to process signatures');
            $createForm.processing = false;
            return;
        }

        const options = {
            preserveScroll: true,
            onSuccess: async (data: any) => {
                let uploadedImagesCount = 0;

                // Upload pending images if any
                if (pendingImageFiles.length > 0) {
                    try {
                        const handoverId = handover?.id || data.props.scaffoldingHandovers?.[0]?.id;
                        if (handoverId) {
                            const csrfToken = $page.props.csrfToken || '';
                            await uploadImages(
                                pendingImageFiles,
                                'App\\Models\\ScaffoldingHandover',
                                handoverId,
                                csrfToken
                            );
                            uploadedImagesCount = pendingImageFiles.length;
                        }
                    } catch (error) {
                        console.error('Error uploading images:', error);
                        toast.error('Scaffolding handover saved but some images failed to upload');
                    }
                }

                // Close modal and reset form
                close();
                $createForm.reset();
                $createForm.processing = false;

                // Reload the page data to show new images and signatures
                router.reload({
                    only: ['scaffoldingHandover', 'scaffoldingHandovers', 'jobImages', 'relatedDocuments'],
                    onSuccess: () => {
                        if (uploadedImagesCount > 0) {
                            toast.success(`Scaffolding handover ${handover ? 'updated' : 'created'} successfully with ${uploadedImagesCount} image(s)!`);
                        } else {
                            toast.success(`Scaffolding handover ${handover ? 'updated' : 'created'} successfully!`);
                        }
                    }
                });
            },
            onError: (errors: any) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error ${handover ? 'updating' : 'creating'} scaffolding handover. ` + message as string);
                }
                $createForm.processing = false;
            },
        };

        if (handover) {
            // Update existing handover
            $createForm.put(route('scaffolding-handovers.update', { job: jobId, scaffolding_handover: handover.id }), options);
        } else {
            // Create new handover
            $createForm.post(route('scaffolding-handovers.store', { job: jobId }), options);
        }
    }
</script>

{#if show}
    <Modal title="{handover ? 'Edit' : 'Add New'} Scaffolding Handover Certificate" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block (block.id)}
                    {@const visibleFields = block.fields.filter(f => f.editable && f.show_in_create !== false)}
                    {#if visibleFields.length > 0}
                        <FormBlock label={block.label}>
                            {#each visibleFields as field (field.id)}
                                {@const fieldState = evaluateFieldDependencies(field.column_name, dependencyMap, $createForm, null)}
                                {#if fieldState.visible}
                                    {#if field.ui_type === 'checkbox'}
                                        <Checkbox
                                            bind:checked={$createForm[field.column_name]}
                                            label={field.label}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else if field.column_name === 'handover_carried_out_by'}
                                        <Select
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            options={handoverPersonOptions}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else if field.ui_type === 'select'}
                                        <Select
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            options={parseFieldOptions(field)}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else if field.ui_type === 'textarea'}
                                        <Textarea
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else if field.ui_type === 'date'}
                                        <Date
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else}
                                        <Input
                                            field={field}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                            onkeydown={() => {}}
                                        />
                                    {/if}

                                    <!-- Render signature pad or existing signature after corresponding field -->
                                    {#if field.column_name === 'handover_carried_out_by'}
                                        <div class="col-span-full">
                                            {#if handover?.inspector_signature?.id && !replaceInspectorSig}
                                                <div class="grid gap-2">
                                                    <span class="text-sm font-medium">Handover Carried Out By Signature</span>
                                                    <div class="flex items-center gap-3">
                                                        <img src={route('signatures.show', { signatureId: handover.inspector_signature.id })} alt="Existing signature" class="h-16 border rounded bg-white p-1" />
                                                        <Button variant="outline" size="sm" onclick={() => { replaceInspectorSig = true; }}>Replace</Button>
                                                    </div>
                                                </div>
                                            {:else}
                                                <Signature
                                                    bind:value={inspectorSignature}
                                                    label="Handover Carried Out By Signature"
                                                    name="inspector_signature"
                                                />
                                            {/if}
                                        </div>
                                    {:else if field.column_name === 'contractor_print_name'}
                                        <div class="col-span-full">
                                            {#if handover?.contractor_signature?.id && !replaceContractorSig}
                                                <div class="grid gap-2">
                                                    <span class="text-sm font-medium">Contractor Signature</span>
                                                    <div class="flex items-center gap-3">
                                                        <img src={route('signatures.show', { signatureId: handover.contractor_signature.id })} alt="Existing signature" class="h-16 border rounded bg-white p-1" />
                                                        <Button variant="outline" size="sm" onclick={() => { replaceContractorSig = true; }}>Replace</Button>
                                                    </div>
                                                </div>
                                            {:else}
                                                <Signature
                                                    bind:value={contractorSignature}
                                                    label="Contractor Signature"
                                                    name="contractor_signature"
                                                />
                                            {/if}
                                        </div>
                                    {:else if field.column_name === 'client_print_name'}
                                        <div class="col-span-full">
                                            {#if $createForm['client_print_name']}
                                                {#if handover?.client_signature?.id && !replaceClientSig}
                                                    <div class="grid gap-2">
                                                        <span class="text-sm font-medium">Client Signature</span>
                                                        <div class="flex items-center gap-3">
                                                            <img src={route('signatures.show', { signatureId: handover.client_signature.id })} alt="Existing signature" class="h-16 border rounded bg-white p-1" />
                                                            <Button variant="outline" size="sm" onclick={() => { replaceClientSig = true; }}>Replace</Button>
                                                        </div>
                                                    </div>
                                                {:else}
                                                    <Signature
                                                        bind:value={clientSignature}
                                                        label="Client Signature"
                                                        name="client_signature"
                                                    />
                                                {/if}
                                            {/if}
                                        </div>
                                    {/if}
                                {/if}
                            {/each}
                        </FormBlock>
                    {/if}
                {/each}

                <FormBlock label="Images">
                    <p class="text-sm text-muted-foreground mb-2">
                        Upload photos from the scaffolding handover inspection
                    </p>
                    <FilePond
                        bind:files={pondFiles}
                        server={null}
                        allowMultiple={true}
                        maxFiles={20}
                        maxFileSize="30MB"
                        labelMaxFileSizeExceeded="File is too large"
                        labelMaxFileSize={"Maximum file size is {filesize}"}
                        onwarning={(error) => {
                            if (error.body) toast.error(error.body);
                        }}
                        name="images"
                        acceptedFileTypes={['image/*']}
                        labelIdle='Drag & Drop your images or <span class="filepond--label-action">Browse</span>'
                        onaddfile={handleFilePondAddFile}
                        onremovefile={handleFilePondRemoveFile}
                    />
                </FormBlock>

                <div class="mt-4 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {#if $createForm.processing}Saving...{:else}{handover ? 'Update' : 'Save'}{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}

<style lang="scss">
    :global(.text-sm) {
        font-size: 0.875rem;
    }

    :global(.text-muted-foreground) {
        color: var(--clr-on-surface-2);
    }

    :global(.mb-2) {
        margin-bottom: 0.5rem;
    }
</style>
